/**
 * Destructive-action confirmation modal.
 *
 * 1. On open, pre-flights `GET /admin/delete-safety/<type>/<id>`.
 * 2. If `safe: false`, shows blockers and an "Archive instead" CTA.
 * 3. If `safe: true`, asks the admin to type the literal word "DELETE" to confirm,
 *    then calls `DELETE /admin/records/<type>/<id>?confirm=true&reason=...`.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Archive, Trash2, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function ConfirmDeleteModal({ open, onClose, recordType, recordId, recordDisplay, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [safety, setSafety] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset transient form state whenever the dialog closes — driven from
  // onOpenChange below, so no useEffect needed and no lint warning.
  const reset = () => {
    setSafety(null);
    setReason("");
    setConfirmText("");
  };

  // Fetch delete-safety whenever the dialog opens for a new record.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/delete-safety/${recordType}/${recordId}`);
        if (!cancelled) setSafety(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, recordType, recordId]);

  const archiveInstead = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/archive/${recordType}/${recordId}`, { reason: reason || null });
      toast.success(`Archived ${recordDisplay}`);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const hardDelete = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ confirm: "true" });
      if (reason) params.set("reason", reason);
      await api.delete(`/admin/records/${recordType}/${recordId}?${params.toString()}`);
      toast.success(`Deleted ${recordDisplay}`);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent data-testid="confirm-delete-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="w-5 h-5" />
            Delete {recordType.replace("_", " ")}
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-slate-900">{recordDisplay}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Checking linked records…
          </div>
        )}

        {!loading && safety && !safety.safe && (
          <div className="space-y-3">
            <div data-testid="delete-blocked-banner" className="rounded-lg border border-rose-200 bg-rose-50 p-4">
              <p className="text-sm font-semibold text-rose-900 mb-2">
                This record cannot be deleted — it has linked data.
              </p>
              <ul className="text-xs text-rose-800 space-y-1">
                {safety.blockers.map((b, i) => (
                  <li key={i} data-testid={`delete-blocker-${b.collection}`}>
                    • {b.reason || `${b.count} linked ${b.collection}`}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-rose-800">{safety.suggestion}</p>
            </div>
            <Textarea
              data-testid="archive-reason-input"
              placeholder="Optional reason for archiving…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                data-testid="archive-instead-btn"
                onClick={archiveInstead}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Archive className="w-4 h-4 mr-1.5" /> Archive instead
              </Button>
            </DialogFooter>
          </div>
        )}

        {!loading && safety && safety.safe && (() => {
          // Phase 2L-A8: for booking deletes specifically, require the
          // admin to type the actual booking reference (e.g. SCWT-123456).
          // For other record types we keep the simpler "DELETE" challenge.
          const useRef = recordType === "booking" && !!recordDisplay;
          const expected = useRef ? recordDisplay : "DELETE";
          return (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900 mb-1">
                ⚠️ This is permanent — no linked records will be affected, but the record itself will be gone.
              </p>
              <p className="text-xs text-amber-800">
                Type {useRef ? "the booking reference" : "the word"} <code className="px-1 py-0.5 rounded bg-amber-100 font-mono">{expected}</code> below to confirm.
              </p>
            </div>
            <Input
              data-testid="confirm-delete-input"
              placeholder={useRef ? `Type ${expected} to confirm` : "Type DELETE to confirm"}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
            />
            <Textarea
              data-testid="delete-reason-input"
              placeholder="Optional reason (audit log)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                data-testid="confirm-delete-btn"
                onClick={hardDelete}
                disabled={confirmText !== expected || busy}
                className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Permanently delete
              </Button>
            </DialogFooter>
          </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDeleteModal;
